import { useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, Loader2, XCircle, Upload, Users } from "lucide-react";
import { getImportResultState } from "@/lib/import-result";
import { parseCsv } from "@/lib/csv";
import {
  apiBulkImportEmployees, apiBulkImportDepartments, apiBulkImportTeams, apiBulkImportPositions,
  type BulkImportResult,
} from "@/lib/api";

import { mapRow, type BulkImportEntity } from "@/lib/bulk-import";
export type { BulkImportEntity } from "@/lib/bulk-import";

interface BulkImportDialogProps {
  open: boolean;
  onClose: () => void;
  entity: BulkImportEntity;
  onComplete?: () => void;
}

const ENTITY_SPEC: Record<BulkImportEntity, { required: string[]; optional: string[]; example: string }> = {
  employees: {
    required: ["name", "email", "jobTitle", "department"],
    optional: ["phone", "team", "location", "startDate", "contractType", "birthday", "nationality", "address", "mainRole", "subRole", "reportsToEmail", "isLineManager"],
    example: "name,email,jobTitle,department,mainRole,subRole,isLineManager,reportsToEmail\nJane Doe,jane@demo.example.com,Engineer,Engineering,user,employee,false,lead@demo.example.com\nTeam Lead,lead@demo.example.com,Director,Management,user,c_level,true,",
  },
  departments: {
    required: ["name"],
    optional: ["description", "headEmail"],
    example: "name,description,headEmail\nEngineering,Builds the product,lead@acme.com",
  },
  teams: {
    required: ["teamName", "leaderEmail"],
    optional: ["memberEmails (semicolon-separated)"],
    example: "teamName,leaderEmail,memberEmails\nPlatform,lead@acme.com,a@acme.com;b@acme.com",
  },
  positions: {
    required: ["title"],
    optional: ["description"],
    example: "title,description\nSenior Engineer,Owns delivery of core services",
  },
};

type Phase = "input" | "uploading" | "done" | "error";

const UPLOAD_STEPS = [
  "bulkImport.step.parsing",
  "bulkImport.step.validating",
  "bulkImport.step.saving",
  "bulkImport.step.finalising",
];

export function BulkImportDialog({ open, onClose, entity, onComplete }: BulkImportDialogProps) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<Phase>("input");
  const [uploadStep, setUploadStep] = useState(0);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const spec = ENTITY_SPEC[entity];

  // Whether a successful import still needs to refresh the parent. We defer the
  // onComplete() call until the dialog is dismissed: onComplete typically triggers
  // an async parent refetch (e.g. loadData) which re-renders the parent and would
  // otherwise tear the success popup back to the empty form mid-display.
  const refreshPendingRef = useRef(false);

  function flushRefresh() {
    if (refreshPendingRef.current) {
      refreshPendingRef.current = false;
      onComplete?.();
    }
  }

  function reset() {
    setText("");
    setPhase("input");
    setUploadStep(0);
    setResult(null);
    setErrorMsg("");
  }

  function handleClose() {
    flushRefresh();
    reset();
    onClose();
  }

  function handleImportMore() {
    flushRefresh();
    reset();
  }

  async function handleImport() {
    const { rows } = parseCsv(text);
    if (rows.length === 0) {
      setErrorMsg(t("bulkImport.noRowsDesc", "Paste a header row followed by at least one record."));
      return;
    }
    const invalid = rows.flatMap((row, index) => {
      const missing = spec.required.filter(key => !row[key.toLowerCase()]?.trim());
      return missing.length ? [t("bulkImport.missingColumns", { row: index + 2, columns: missing.join(", ") })] : [];
    });
    if (invalid.length) { setErrorMsg(invalid.join("\n")); return; }
    const payload = rows.map((r) => mapRow(entity, r));

    setPhase("uploading");
    setUploadStep(0);

    // Animate through steps while the real request runs
    const stepInterval = setInterval(() => {
      setUploadStep((s) => {
        const next = s + 1;
        if (next >= UPLOAD_STEPS.length - 1) clearInterval(stepInterval);
        return next;
      });
    }, 600);

    try {
      let res: BulkImportResult;
      if (entity === "employees") res = await apiBulkImportEmployees(payload);
      else if (entity === "departments") res = await apiBulkImportDepartments(payload);
      else if (entity === "positions") res = await apiBulkImportPositions(payload);
      else res = await apiBulkImportTeams(payload);

      clearInterval(stepInterval);
      setUploadStep(UPLOAD_STEPS.length - 1);

      // Brief pause so the last step is visible
      await new Promise((r) => setTimeout(r, 400));

      setResult(res);
      setPhase("done");
      // Defer the parent refresh until the dialog is dismissed (see flushRefresh).
      refreshPendingRef.current = res.imported > 0;
    } catch (err: any) {
      clearInterval(stepInterval);
      setErrorMsg(err.code === "common.moduleDisabled" || err.message === "Module not enabled for this tenant"
        ? t("common.moduleDisabled") : err.message ?? t("common.error"));
      setPhase("error");
    }
  }

  const entityLabel = t(`bulkImport.entity.${entity}`, entity);
  const resultState = result ? getImportResultState(result.imported, result.skipped) : "success";
  const ResultIcon = resultState === "success" ? CheckCircle2 : resultState === "partial" ? AlertTriangle : XCircle;
  const resultColor = resultState === "success" ? "text-emerald-600 bg-emerald-500/10"
    : resultState === "partial" ? "text-amber-600 bg-amber-500/10" : "text-destructive bg-destructive/10";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">

        {/* ── Input phase ─────────────────────────────────────────────────── */}
        {phase === "input" && (
          <>
            <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-primary" />
                {t(`bulkImport.title.${entity}`, `Import ${entityLabel}`)}
              </DialogTitle>
              <DialogDescription>
                {t("bulkImport.description", "Paste CSV data below. The first row must be a header row with column names.")}
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
              <div className="rounded-lg border bg-secondary/30 p-3 text-xs space-y-1.5">
                <div>
                  <span className="font-semibold text-foreground">{t("bulkImport.requiredColumns", "Required")}: </span>
                  <span className="text-muted-foreground">{spec.required.join(", ")}</span>
                </div>
                <div>
                  <span className="font-semibold text-foreground">{t("bulkImport.optionalColumns", "Optional")}: </span>
                  <span className="text-muted-foreground">{spec.optional.join(", ")}</span>
                </div>
                {entity === "employees" && <p className="text-muted-foreground leading-relaxed">{t("bulkImport.employeeInstructions")}</p>}
                {entity === "teams" && <p className="text-muted-foreground leading-relaxed">{t("bulkImport.teamInstructions")}</p>}
                <pre className="mt-2 whitespace-pre-wrap rounded bg-background border px-2 py-1.5 text-[11px] text-muted-foreground font-mono">{spec.example}</pre>
              </div>

              <Textarea
                value={text}
                onChange={(e) => { setText(e.target.value); setErrorMsg(""); }}
                placeholder={spec.example}
                className="min-h-[160px] font-mono text-xs"
              />

              {errorMsg && (
                <p className="whitespace-pre-line text-sm text-destructive flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {errorMsg}
                </p>
              )}
            </div>

            <DialogFooter className="px-6 py-4 border-t shrink-0">
              <Button variant="outline" onClick={handleClose}>{t("common.cancel", "Cancel")}</Button>
              <Button onClick={handleImport} disabled={!text.trim()}>
                {t("bulkImport.importBtn", "Import")}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── Uploading phase ─────────────────────────────────────────────── */}
        {phase === "uploading" && (
          <div className="flex flex-col items-center justify-center gap-6 px-8 py-16 text-center">
            <div className="relative">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
              </div>
            </div>
            <div>
              <p className="text-lg font-semibold text-foreground">
                {t("bulkImport.uploading", "Uploading…")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t("bulkImport.pleaseWait", "Please don't close this window.")}
              </p>
            </div>
            <div className="w-full max-w-xs space-y-2">
              {UPLOAD_STEPS.map((key, i) => {
                const done = i < uploadStep;
                const active = i === uploadStep;
                return (
                  <div key={key} className={`flex items-center gap-3 text-sm transition-all ${active ? "text-foreground font-medium" : done ? "text-emerald-600" : "text-muted-foreground/40"}`}>
                    {done ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                    ) : active ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
                    ) : (
                      <div className="h-4 w-4 shrink-0 rounded-full border-2 border-current" />
                    )}
                    {t(key, key.split(".").pop() ?? key)}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Error phase ─────────────────────────────────────────────────── */}
        {phase === "error" && (
          <>
            <div className="flex flex-col items-center justify-center gap-4 px-8 py-12 text-center">
              <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
              <p className="text-lg font-semibold text-foreground">{t("bulkImport.failed", "Import failed")}</p>
              <p className="text-sm text-muted-foreground max-w-sm">{errorMsg}</p>
            </div>
            <DialogFooter className="px-6 py-4 border-t shrink-0">
              <Button variant="outline" onClick={handleClose}>{t("common.close", "Close")}</Button>
              <Button onClick={reset}>{t("bulkImport.tryAgain", "Try Again")}</Button>
            </DialogFooter>
          </>
        )}

        {/* ── Done phase ──────────────────────────────────────────────────── */}
        {phase === "done" && result && (
          <>
            <div className="flex flex-col items-center gap-5 px-8 pt-10 pb-4 text-center">
              <div className="relative">
                <div className={`h-20 w-20 rounded-full flex items-center justify-center ${resultColor}`}>
                  <ResultIcon className="h-10 w-10" />
                </div>
              </div>
              <div>
                <p className="text-xl font-bold text-foreground">
                  {t(`bulkImport.result.${resultState}Title`)}
                </p>
                <p className="text-muted-foreground mt-1 text-sm">
                  {t(`bulkImport.result.${resultState}Message`, { imported: result.imported, skipped: result.skipped })}
                </p>
              </div>

              {/* Stats */}
              <div className="flex gap-6">
                <div className="flex flex-col items-center gap-1">
                  <span className={`text-3xl font-bold ${resultColor}`}>{result.imported}</span>
                  <span className="text-xs text-muted-foreground">{t("bulkImport.imported", "Imported")}</span>
                </div>
                {result.skipped > 0 && (
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-3xl font-bold text-amber-500">{result.skipped}</span>
                    <span className="text-xs text-muted-foreground">{t("bulkImport.skipped", "Skipped")}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-3 min-h-0">
              {/* Skipped rows detail */}
              {result.details.skipped.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/40 p-3 space-y-2">
                  <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400 text-xs font-semibold">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t("bulkImport.skippedRows", "Skipped rows")} ({result.details.skipped.length})
                  </div>
                  <ul className="max-h-36 overflow-y-auto text-xs text-muted-foreground space-y-0.5">
                    {result.details.skipped.map((s, idx) => (
                      <li key={idx} className="flex gap-2">
                        <span className="text-foreground/50 shrink-0">{t("bulkImport.rowLabel", "Row")} {s.row}:</span>
                        <span>{t(s.reason, { defaultValue: s.reason })}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Temp passwords for employees */}
              {entity === "employees" && result.details.imported.some((r) => r.tempPassword) && (
                <div className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                    <Users className="h-3.5 w-3.5" />
                    {t("bulkImport.tempPasswords", "Temporary passwords")}
                  </div>
                  <ul className="max-h-36 overflow-y-auto text-xs text-muted-foreground font-mono space-y-0.5">
                    {result.details.imported.filter((r) => r.tempPassword).map((r, idx) => (
                      <li key={idx}>{r.email}: <span className="font-semibold text-foreground">{r.tempPassword}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter className="px-6 py-4 border-t shrink-0 flex gap-2 sm:justify-between">
              <Button variant="outline" size="sm" onClick={handleImportMore}>
                {t("bulkImport.importMore", "Import more")}
              </Button>
              <Button onClick={handleClose}>
                {t("common.done", "Done")}
              </Button>
            </DialogFooter>
          </>
        )}

      </DialogContent>
    </Dialog>
  );
}
